/**
 * SettingsTab - World-Class Minimalist Design
 * Tenant settings with clean, elegant UI
 */
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, Users, Briefcase, FolderKanban, Users2, ChevronDown, Check, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Supported currencies
const CURRENCIES = [
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone', flag: '🇳🇴' },
  { code: 'USD', symbol: '$', name: 'US Dollar', flag: '🇺🇸' },
  { code: 'EUR', symbol: '€', name: 'Euro', flag: '🇪🇺' },
  { code: 'GBP', symbol: '£', name: 'British Pound', flag: '🇬🇧' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona', flag: '🇸🇪' },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone', flag: '🇩🇰' },
  { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc', flag: '🇨🇭' },
  { code: 'CAD', symbol: '$', name: 'Canadian Dollar', flag: '🇨🇦' },
  { code: 'AUD', symbol: '$', name: 'Australian Dollar', flag: '🇦🇺' },
];

const translations = {
  en: {
    settings: 'Settings',
    subtitle: 'Manage your organization settings',
    organizationInfo: 'Organization',
    companyName: 'Company Name',
    notSet: 'Not set',
    planType: 'Plan',
    status: 'Status',
    active: 'Active',
    statistics: 'Overview',
    users: 'Users',
    cases: 'Cases',
    teams: 'Teams',
    departments: 'Departments',
    loading: 'Loading...',
    currency: 'Currency',
    currencyDescription: 'Used for billing and invoices',
    currencyUpdated: 'Currency updated',
    currencyError: 'Failed to update'
  },
  no: {
    settings: 'Innstillinger',
    subtitle: 'Administrer organisasjonsinnstillinger',
    organizationInfo: 'Organisasjon',
    companyName: 'Firmanavn',
    notSet: 'Ikke satt',
    planType: 'Abonnement',
    status: 'Status',
    active: 'Aktiv',
    statistics: 'Oversikt',
    users: 'Brukere',
    cases: 'Saker',
    teams: 'Team',
    departments: 'Avdelinger',
    loading: 'Laster...',
    currency: 'Valuta',
    currencyDescription: 'Brukes for fakturering',
    currencyUpdated: 'Valuta oppdatert',
    currencyError: 'Kunne ikke oppdatere'
  }
};

interface SettingsTabProps {
  tenant: any;
  onRefresh: () => void;
  language?: string;
}

const SettingsTab: React.FC<SettingsTabProps> = ({ tenant, onRefresh, language = 'no' }) => {
  const t = translations[language] || translations.no;
  const [selectedCurrency, setSelectedCurrency] = useState(tenant?.currency || 'NOK');
  const [savingCurrency, setSavingCurrency] = useState(false);
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (tenant?.currency) {
      setSelectedCurrency(tenant.currency);
    }
  }, [tenant?.currency]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowCurrencyDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCurrencyChange = async (currencyCode: string) => {
    if (currencyCode === selectedCurrency) {
      setShowCurrencyDropdown(false);
      return;
    }
    
    setSavingCurrency(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/admin/tenant`,
        { currency: currencyCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedCurrency(currencyCode);
      toast.success(t.currencyUpdated);
      onRefresh();
    } catch (error) {
      toast.error(t.currencyError);
    } finally {
      setSavingCurrency(false);
      setShowCurrencyDropdown(false);
    }
  };

  const currentCurrency = CURRENCIES.find(c => c.code === selectedCurrency) || CURRENCIES[0];

  if (!tenant) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const stats = [
    { label: t.users, value: tenant.statistics?.users || 0, icon: Users },
    { label: t.cases, value: tenant.statistics?.cases || 0, icon: FolderKanban },
    { label: t.teams, value: tenant.statistics?.teams || 0, icon: Users2 },
    { label: t.departments, value: tenant.statistics?.departments || 0, icon: Briefcase }
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-slate-900">{t.settings}</h2>
        <p className="text-slate-500 text-sm mt-1">{t.subtitle}</p>
      </div>

      <div className="space-y-4 max-w-2xl">
        {/* Organization Info */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-50/50 rounded-2xl p-5"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 bg-white rounded-xl flex items-center justify-center shadow-sm">
              <Building2 className="w-4 h-4 text-slate-600" />
            </div>
            <h3 className="font-medium text-slate-900">{t.organizationInfo}</h3>
          </div>
          
          <div className="grid grid-cols-3 gap-6">
            <div>
              <label className="text-xs text-slate-400 block mb-1">{t.companyName}</label>
              <p className="text-sm font-medium text-slate-900">{tenant.company_name || tenant.name || t.notSet}</p>
            </div>
            
            <div>
              <label className="text-xs text-slate-400 block mb-1">{t.planType}</label>
              <span className="inline-flex px-2.5 py-1 bg-slate-900 text-white rounded-lg text-xs font-medium">
                {tenant.plan_type?.toUpperCase() || 'SOLO'}
              </span>
            </div>
            
            <div>
              <label className="text-xs text-slate-400 block mb-1">{t.status}</label>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                {t.active}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Currency Setting */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-slate-50/50 rounded-2xl p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-medium text-slate-900">{t.currency}</h3>
              <p className="text-xs text-slate-400 mt-0.5">{t.currencyDescription}</p>
            </div>
          </div>
          
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
              disabled={savingCurrency}
              className="flex items-center justify-between px-4 py-3 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-all disabled:opacity-50 w-full max-w-xs"
            >
              <div className="flex items-center gap-3">
                <span className="text-lg">{currentCurrency.flag}</span>
                <div className="text-left">
                  <p className="text-sm font-medium text-slate-900">{currentCurrency.code}</p>
                  <p className="text-xs text-slate-400">{currentCurrency.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-500">{currentCurrency.symbol}</span>
                {savingCurrency ? (
                  <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
                ) : (
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showCurrencyDropdown ? 'rotate-180' : ''}`} />
                )}
              </div>
            </button>
            
            <AnimatePresence>
              {showCurrencyDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  className="absolute top-full left-0 mt-2 w-full max-w-xs bg-white border border-slate-200 rounded-xl shadow-xl z-50 max-h-64 overflow-y-auto"
                >
                  {CURRENCIES.map((currency) => (
                    <button
                      key={currency.code}
                      onClick={() => handleCurrencyChange(currency.code)}
                      className={`w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 transition-colors first:rounded-t-xl last:rounded-b-xl ${
                        currency.code === selectedCurrency ? 'bg-slate-50' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-base">{currency.flag}</span>
                        <div className="text-left">
                          <p className="text-sm font-medium text-slate-900">{currency.code}</p>
                          <p className="text-xs text-slate-400">{currency.name}</p>
                        </div>
                      </div>
                      {currency.code === selectedCurrency && (
                        <Check className="w-4 h-4 text-emerald-500" />
                      )}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Statistics */}
        {tenant.statistics && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-slate-50/50 rounded-2xl p-5"
          >
            <h3 className="font-medium text-slate-900 mb-4">{t.statistics}</h3>
            
            <div className="grid grid-cols-4 gap-3">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.15 + index * 0.03 }}
                  className="text-center p-4 bg-white rounded-xl"
                >
                  <stat.icon className="w-5 h-5 text-slate-400 mx-auto mb-2" />
                  <p className="text-2xl font-semibold text-slate-900">{stat.value}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default SettingsTab;
